import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePerson, useArchivePerson, useReprocessApplication, useArchiveApplication, useForceReview } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../contexts/ToastContext';
import { StatusBadge, type ApplicationStatus } from '../components/StatusBadge';
import { ReviewCard } from '../components/ReviewCard';
import { ReviewVersionHistory } from '../components/ReviewVersionHistory';
import { FeedbackPanel } from '../components/FeedbackPanel';
import { SyncStatusCard } from '../components/SyncStatusCard';
import { ChatDrawer } from '../components/ChatDrawer';
import { ResumeViewer } from '../components/ResumeViewer';
import { Button } from '../components/ui';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  Trash2,
  FileText,
  AlertCircle,
  Clock,
  Archive,
  PlayCircle,
  MessageCircle,
  MessageSquare,
  ArrowRight,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { PIIAuditPanel } from '../components/PIIAuditPanel';
import { InlineReviewFeedback } from '../components/InlineReviewFeedback';
import { SkeletonDetail } from '../components/ui/Skeleton';
import { api } from '../api/client';
import type { PersonApplication, ApplicationWithDetails, StageTransition } from '../types';

// US-232 Task 1: Build Arbie's top-level summary from the most recent non-archived review
// PersonReview doesn't carry archivedAt (it's filtered server-side); all reviews here are active
function buildArbieSummary(applications: PersonApplication[]): string | null {
  const summaries = applications
    .flatMap(a => a.reviews)
    .filter(r => r.summary)
    .map(r => r.summary);
  return summaries.length > 0 ? summaries[0] : null;
}

// US-232 Task 2: Helper to get the most relevant review for an application
function latestReview(app: PersonApplication) {
  return app.reviews[0] ?? null;
}

function stageBadgeColor(srStage: string | null): string {
  const s = (srStage ?? '').toUpperCase();
  if (s === 'NEW') return 'bg-stone-100 dark:bg-gray-700 text-stone-600 dark:text-stone-300';
  if (s === 'IN_REVIEW') return 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-400';
  if (s === 'INTERVIEW') return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400';
  if (s === 'OFFER') return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400';
  if (s === 'REJECTED') return 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-400';
  return 'bg-stone-100 dark:bg-gray-700 text-stone-600 dark:text-stone-300';
}

// US-232 Task 3: Extract deduplicated skill tags from review strengths
function extractSkillTags(applications: PersonApplication[]): string[] {
  const seen = new Set<string>();
  const skills: string[] = [];
  for (const app of applications) {
    for (const review of app.reviews) {
      if (!review.strengths) continue;
      const parts = review.strengths.split(/[,\n•\-]+/).map(s => s.trim()).filter(s => s.length > 1 && s.length < 40);
      for (const part of parts) {
        const key = part.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          skills.push(part);
        }
      }
    }
  }
  return skills.slice(0, 12);
}

function ApplicationSection({ application }: { application: PersonApplication }) {
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAdmin = user?.role === 'admin';

  // Lazy-load full application detail on first expand
  const [fullApp, setFullApp] = useState<ApplicationWithDetails | null>(null);
  const [fullAppLoading, setFullAppLoading] = useState(false);
  const [fullAppError, setFullAppError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  // Chat drawer state
  const [isChatOpen, setIsChatOpen] = useState(false);
  // Feedback form state
  const [feedbackFormOpen, setFeedbackFormOpen] = useState(false);
  const feedbackRef = useRef<HTMLDivElement>(null);
  // Screening answers state
  const [isScreeningExpanded, setIsScreeningExpanded] = useState(false);

  const reprocessMutation = useReprocessApplication();
  const archiveMutation = useArchiveApplication();
  const forceReviewMutation = useForceReview();

  // Fetch full app data on first expand
  useEffect(() => {
    if (expanded && !hasFetched.current) {
      hasFetched.current = true;
      setFullAppLoading(true);
      api.getApplication(application.id)
        .then((data) => {
          setFullApp(data);
        })
        .catch((err) => {
          console.error('Failed to load full application:', err);
          setFullAppError('Failed to load application details');
        })
        .finally(() => {
          setFullAppLoading(false);
        });
    }
  }, [expanded, application.id]);

  const handleReprocess = async () => {
    try {
      await reprocessMutation.mutateAsync({
        id: application.id,
        reason: 'Manual reprocess from person detail page',
      });
      showToast('Review requested', 'success');
      // Refresh full app data
      const refreshed = await api.getApplication(application.id);
      setFullApp(refreshed);
    } catch (err) {
      console.error('Reprocess failed:', err);
      showToast('Failed to reprocess', 'error');
    }
  };

  const handleArchive = async () => {
    if (
      window.confirm(
        'Are you sure you want to archive this application? It will be set to stub status.'
      )
    ) {
      try {
        await archiveMutation.mutateAsync(application.id);
        showToast('Application archived successfully', 'success');
      } catch (err) {
        console.error('Archive failed:', err);
        showToast('Failed to archive application', 'error');
      }
    }
  };

  const handleForceReview = async () => {
    if (
      window.confirm(
        'Force an AI review for this candidate? This will override the human-review skip and queue the candidate for AI analysis.'
      )
    ) {
      try {
        await forceReviewMutation.mutateAsync(application.id);
        showToast('AI review queued (force override)', 'success');
        const refreshed = await api.getApplication(application.id);
        setFullApp(refreshed);
      } catch (err) {
        console.error('Force review failed:', err);
        showToast('Failed to force AI review', 'error');
      }
    }
  };

  // Use full app data when available, otherwise fall back to PersonApplication data
  const reviews = fullApp?.reviews ?? application.reviews;
  const screeningAnswers = (fullApp?.screeningAnswers ?? application.screeningAnswers)
    ? Object.entries(fullApp?.screeningAnswers ?? application.screeningAnswers ?? {})
    : [];
  const hasMultipleVersions = reviews.length > 1 ||
    reviews.some(r => (r.version ?? 1) > 1 || r.archivedAt !== null);
  const isHumanReviewed = fullApp?.humanReviewDetected && fullApp?.skipReason === 'human_reviewed';
  const candidateName = fullApp
    ? `${fullApp.candidate.firstName} ${fullApp.candidate.lastName}`
    : '';

  return (
    <div className="card dark:bg-gray-800 dark:border-gray-700 overflow-hidden">
      {/* Application header — two click zones */}
      <div className="flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-5 py-3 sm:py-4">
        {/* Left side: job title — click to expand/collapse review */}
        <div
          className="flex items-center gap-2 sm:gap-3 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
            <Briefcase className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100 truncate">
              {application.jobTitle}
            </h3>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-0.5">
              <StatusBadge status={application.status as ApplicationStatus} />
              {application.srStage && (
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                  {application.srStage}
                </span>
              )}
              <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">
                Applied {formatDistanceToNow(new Date(application.createdAt), { addSuffix: true })}
              </span>
            </div>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />}
        </div>

        {/* Right side: score — click to navigate to application page */}
        <div
          className="flex items-center gap-1 sm:gap-2 shrink-0 cursor-pointer rounded-lg px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          onClick={() => navigate(`/applications/${application.id}`)}
          title="View application"
        >
          {application.reviews.length > 0 && (
            <span className={`text-lg font-bold tabular-nums ${
              application.reviews[0].overallScore * 2 >= 80 ? 'text-emerald-600 dark:text-emerald-400'
              : application.reviews[0].overallScore * 2 >= 60 ? 'text-blue-600 dark:text-blue-400'
              : application.reviews[0].overallScore * 2 >= 40 ? 'text-amber-600 dark:text-amber-400'
              : 'text-red-600 dark:text-red-400'
            }`}>
              {(application.reviews[0].overallScore / 10).toFixed(1)}
              <span className="text-sm font-normal opacity-60">/5</span>
            </span>
          )}
          <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
        </div>
      </div>

      {/* Expanded: full application detail inline */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-3 sm:px-5 py-3 sm:py-4 space-y-4">

          {/* Last reviewed meta */}
          {reviews.length > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Last reviewed {formatDistanceToNow(new Date(reviews[0].createdAt), { addSuffix: true })}
              {' '}by {reviews[0].reviewer || 'AI'}
              {reviews[0].modelTier ? ` (${reviews[0].modelTier})` : ''}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              onClick={handleReprocess}
              loading={reprocessMutation.isPending}
              leftIcon={<RefreshCw className="h-4 w-4" />}
              className="text-sm"
            >
              Re-run AI Review
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsChatOpen(true)}
              leftIcon={<MessageCircle className="h-4 w-4" />}
              className="text-sm"
            >
              Chat
            </Button>
            {isHumanReviewed && (
              <Button
                variant="secondary"
                onClick={handleForceReview}
                loading={forceReviewMutation.isPending}
                leftIcon={<PlayCircle className="h-4 w-4" />}
                className="text-sm text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
              >
                Override and Re-review
              </Button>
            )}
            {application.status !== 'stub' && (
              <Button
                variant="secondary"
                onClick={handleArchive}
                loading={archiveMutation.isPending}
                leftIcon={<Archive className="h-4 w-4" />}
                className="text-sm text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
              >
                Archive
              </Button>
            )}
          </div>

          {/* Error message */}
          {application.errorMessage && (
            <div className="rounded-md bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  {isAdmin ? (
                    <>
                      <p className="text-xs font-semibold uppercase tracking-wide text-red-400 dark:text-red-500 mb-1">🔒 Admin only</p>
                      {application.errorMessage.includes('429') || application.errorMessage.includes('rate') ? (
                        <p>AI model rate limited — temporary, will resolve on retry.</p>
                      ) : application.errorMessage.includes('[object Object]') ? (
                        <p>An unexpected error occurred during processing.</p>
                      ) : (
                        <p className="break-words">{application.errorMessage.length > 300
                          ? application.errorMessage.substring(0, 300) + '...'
                          : application.errorMessage}
                        </p>
                      )}
                    </>
                  ) : (
                    <p>AI review could not be completed. The team has been notified.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Loading state */}
          {fullAppLoading && (
            <div className="flex items-center justify-center py-8 text-sm text-gray-400 dark:text-gray-500">
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              Loading application details...
            </div>
          )}

          {/* Load error fallback */}
          {fullAppError && !fullAppLoading && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              {fullAppError} — showing basic review data below.
            </div>
          )}

          {/* Full AI Review */}
          {reviews.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400 dark:text-gray-500">
              <FileText className="mx-auto h-6 w-6 mb-1" />
              No AI review yet
            </div>
          ) : hasMultipleVersions ? (
            <ReviewVersionHistory
              reviews={reviews as Parameters<typeof ReviewVersionHistory>[0]['reviews']}
              screeningAnswers={fullApp?.screeningAnswers ?? application.screeningAnswers}
            />
          ) : (
            <ReviewCard
              review={reviews[0] as Parameters<typeof ReviewCard>[0]['review']}
              showFullDetails={true}
              screeningAnswers={fullApp?.screeningAnswers ?? application.screeningAnswers}
            />
          )}

          {/* Inline Feedback (quick rating) */}
          {reviews.length > 0 && (
            <InlineReviewFeedback
              reviewId={reviews[0].id}
              applicationId={application.id}
              navigateOnSubmit={false}
            />
          )}

          {/* Feedback Panel */}
          <div ref={feedbackRef} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                setFeedbackFormOpen(!feedbackFormOpen);
              }}
              className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Submit Feedback &amp; Request Revision
                </span>
              </div>
              {feedbackFormOpen
                ? <ChevronUp className="h-4 w-4 text-gray-400" />
                : <ChevronDown className="h-4 w-4 text-gray-400" />
              }
            </button>
            {feedbackFormOpen && (
              <div className="border-t border-gray-100 dark:border-gray-700 p-3 sm:p-4">
                <FeedbackPanel
                  applicationId={application.id}
                  forceShowForm={feedbackFormOpen}
                  onFormToggle={setFeedbackFormOpen}
                />
              </div>
            )}
          </div>

          {/* Screening Answers */}
          {screeningAnswers.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsScreeningExpanded(!isScreeningExpanded)}
                className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Screening Answers ({screeningAnswers.length})
                </span>
                {isScreeningExpanded
                  ? <ChevronUp className="h-4 w-4 text-gray-400" />
                  : <ChevronDown className="h-4 w-4 text-gray-400" />
                }
              </button>
              {isScreeningExpanded && (
                <div className="border-t border-gray-100 dark:border-gray-700 p-3 sm:p-4 space-y-3">
                  {screeningAnswers.map(([question, answer], index) => (
                    <div key={index} className="pb-3 border-b border-gray-100 dark:border-gray-700 last:border-0 last:pb-0">
                      <dt className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{question}</dt>
                      <dd className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{answer}</dd>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sync Status */}
          {fullApp && (
            <SyncStatusCard
              applicationId={application.id}
              reviewId={fullApp.reviews[0]?.id}
              syncInfo={fullApp.syncInfo}
              srReviewId={fullApp.reviews[0]?.srReviewId}
            />
          )}

          {/* Stage History */}
          {fullApp?.transitionHistory && fullApp.transitionHistory.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Stage History
              </h4>
              <div className="space-y-1.5">
                {(fullApp.transitionHistory as StageTransition[]).map((transition, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                      {transition.from || 'initial'}
                    </span>
                    <ArrowRight className="h-3 w-3 flex-shrink-0 text-gray-400" />
                    <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                      {transition.to}
                    </span>
                    <span className="ml-auto text-gray-400 dark:text-gray-500 whitespace-nowrap">
                      {new Date(transition.timestamp).toLocaleDateString()}
                    </span>
                    {transition.reason && (
                      <span className="text-amber-600 dark:text-amber-400 italic">
                        ({transition.reason})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action links at bottom */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
            <Link
              to={`/applications/${application.id}`}
              className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
            >
              View application <ExternalLink className="h-3 w-3" />
            </Link>
            <Link
              to={`/jobs/${application.jobId}`}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 flex items-center gap-1"
            >
              View job <ExternalLink className="h-3 w-3" />
            </Link>
            {application.srApplicationId && (
              <a
                href={`https://www.smartrecruiters.com/app/people/applications/${application.srApplicationId}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 flex items-center gap-1"
              >
                View in SR <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Chat Drawer */}
      <ChatDrawer
        applicationId={application.id}
        candidateName={candidateName || application.jobTitle}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />
    </div>
  );
}

export default function PersonDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const personId = parseInt(id || '0', 10);

  const { data: person, isLoading, error, refetch } = usePerson(personId);
  const archivePersonMutation = useArchivePerson();

  const handleDeletePerson = async () => {
    if (!person) return;
    const name = `${person.firstName} ${person.lastName}`.trim();
    if (!window.confirm(`Archive "${name}"? Their applications will be removed from active views.\n\nThis can be reversed by an admin.`)) return;
    try {
      await archivePersonMutation.mutateAsync(personId);
      navigate('/people');
    } catch (err) {
      const error = err as Error;
      console.error('Archive failed:', error.message);
    }
  };

  if (isLoading) {
    return <SkeletonDetail showSidebar={false} sections={3} />;
  }

  if (error || !person) {
    const errorMsg = error
      ? (error instanceof Error ? error.message : typeof error === 'string' ? error : JSON.stringify(error))
      : 'Person not found';
    return (
      <div className="card p-6 dark:bg-gray-800 dark:border-gray-700">
        <div className="text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-red-400 dark:text-red-500 mb-3" />
          <p className="text-red-600 dark:text-red-400 font-medium">
            {error ? 'Failed to load person' : 'Person not found'}
          </p>
          {error && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {errorMsg}
            </p>
          )}
          <div className="flex items-center justify-center gap-3 mt-4">
            <button onClick={() => refetch()} className="btn btn-primary">
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Retry
            </button>
            <button onClick={() => navigate('/people')} className="btn btn-secondary">
              Back to People
            </button>
          </div>
        </div>
      </div>
    );
  }

  const name = `${person.firstName} ${person.lastName}`.trim();

  return (
    <div className="space-y-4 sm:space-y-6 max-w-4xl">
      {/* Back link */}
      <button
        onClick={() => navigate('/people')}
        className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to People
      </button>

      {/* Person Header */}
      <div className="card p-4 sm:p-6 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Large avatar */}
          <div className="flex h-11 w-11 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 text-base sm:text-lg font-bold text-primary-700 dark:text-primary-300">
            {person.firstName.charAt(0)}{person.lastName.charAt(0)}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 truncate">{name}</h1>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={`https://www.smartrecruiters.com/app/people/candidates/${person.srCandidateId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary !px-2 sm:!px-4"
                  title="View in SmartRecruiters"
                >
                  <ExternalLink className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">SR</span>
                </a>
                <button
                  onClick={() => refetch()}
                  className="btn btn-secondary !px-2 sm:!px-4"
                  title="Refresh"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  onClick={handleDeletePerson}
                  disabled={archivePersonMutation.isPending}
                  className="btn btn-secondary !px-2 sm:!px-4 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                  title="Archive this person"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-1.5 sm:mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-x-4 sm:gap-y-1 text-sm text-gray-500 dark:text-gray-400">
              <a
                href={`mailto:${person.email}`}
                className="flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate"
              >
                <Mail className="h-4 w-4 shrink-0" />
                <span className="truncate">{person.email}</span>
              </a>
              {person.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-4 w-4 shrink-0" />
                  {person.phone}
                </span>
              )}
              {person.location && (
                <span className="flex items-center gap-1 truncate">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span className="truncate">{person.location}</span>
                </span>
              )}
            </div>

            <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-gray-400 dark:text-gray-500">
              {person.source && <span>Source: {person.source}</span>}
              <span>Added {formatDistanceToNow(new Date(person.createdAt), { addSuffix: true })}</span>
              <span className="hidden sm:inline">SR ID: {person.srCandidateId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* US-232 Task 1: Arbie's Summary */}
      {(() => {
        const summary = buildArbieSummary(person.applications);
        if (!summary) return null;
        const appCount = person.applications.length;
        return (
          <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-sm text-stone-800 dark:text-stone-100">Arbie's summary</span>
              <span className="text-xs bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-400 px-1.5 py-0.5 rounded font-medium">
                across {appCount} application{appCount !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-sm text-stone-700 dark:text-stone-200 leading-relaxed">{summary}</p>
          </div>
        );
      })()}

      {/* US-232 Task 2: Application History */}
      {person.applications.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-xl p-4">
          <h3 className="font-semibold text-sm text-stone-800 dark:text-stone-100 mb-3">Application History</h3>
          <div className="space-y-2">
            {person.applications.map(app => {
              const review = latestReview(app);
              const createdDate = new Date(app.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
              return (
                <div key={app.id} className="flex items-center justify-between gap-2 py-2 border-b border-stone-100 dark:border-gray-700 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-800 dark:text-stone-100 truncate">{app.jobTitle}</p>
                    <p className="text-xs text-stone-400 dark:text-stone-500">{app.jobDepartment ? `${app.jobDepartment} · ` : ''}{createdDate}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${stageBadgeColor(app.srStage)}`}>
                      {(app.srStage ?? 'Unknown').replace('_', ' ')}
                    </span>
                    {review?.overallScore != null && (
                      <span className="text-xs font-semibold text-stone-700 dark:text-stone-200">
                        {review.overallScore}/50
                      </span>
                    )}
                    {app.jobStatus === 'active' && (
                      <span className="text-[10px] bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded font-medium">
                        In progress
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* US-232 Task 3: Skills & Signals */}
      {(() => {
        const skills = extractSkillTags(person.applications);
        if (skills.length === 0) return null;
        return (
          <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-xl p-4">
            <h3 className="font-semibold text-sm text-stone-800 dark:text-stone-100 mb-3">Skills & Signals</h3>
            <div className="flex flex-wrap gap-1.5">
              {skills.map((skill, i) => (
                <span
                  key={i}
                  className="text-xs px-2 py-1 bg-stone-100 dark:bg-gray-700 text-stone-700 dark:text-stone-200 rounded-md font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Applications */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
          Applications ({person.applications.length})
        </h2>

        {person.applications.length === 0 ? (
          <div className="card p-8 text-center dark:bg-gray-800 dark:border-gray-700">
            <Briefcase className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-gray-500">No applications found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {person.applications.map((app) => (
              <ApplicationSection key={app.id} application={app} />
            ))}
          </div>
        )}
      </div>

      {/* Resume section */}
      <ResumeViewer
        resumeText={person.resumeText}
        applicationId={person.applications?.[0]?.id}
        candidateId={person.id}
        onResumeUpdated={() => refetch()}
      />

      {/* PII Redaction Audit */}
      {person.sanitizedResumeText && person.applications.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
            PII Redaction Audit
          </h2>
          <PIIAuditPanel
            originalText={person.resumeText}
            sanitizedText={person.sanitizedResumeText}
            applicationId={person.applications[0].id}
            candidateName={name}
            processedAt={person.resumeProcessedAt}
          />
        </div>
      )}
    </div>
  );
}
